import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, errorMessage } from '../../context/AuthContext';
import RichEditor from '../../components/RichEditor';

export default function AdminNoteEdit() {
  const { id } = useParams();
  const isNew = !id;
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const presetSubject = Number(search.get('sujet')) || '';   // ouvert depuis un sujet : il est déjà choisi
  const [form, setForm] = useState({ subject_id: presetSubject, title: '', content: '', published: true });
  const [subjects, setSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [flash, setFlash] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const loadSubjects = () => authFetch('/api/admin/notes/subjects')
    .then(r => r.ok ? r.json() : Promise.reject()).then(setSubjects).catch(() => setFlash({ type: 'error', text: 'Sujets non chargés' }));

  useEffect(() => {
    loadSubjects();
    if (isNew) return;
    authFetch(`/api/admin/notes/${id}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('note introuvable')))
      .then(n => setForm({ subject_id: n.subject_id, title: n.title, content: n.content || '', published: !!n.published }))
      .catch(e => setFlash({ type: 'error', text: `Erreur : ${e.message}` }));
    // eslint-disable-next-line
  }, [id]);

  // crée le sujet saisi et le sélectionne tout de suite
  const addSubject = async () => {
    if (!newSubject.trim()) return;
    try {
      const r = await authFetch('/api/admin/notes/subjects', { method: 'POST', body: JSON.stringify({ name: newSubject }) });
      if (!r.ok) throw new Error(await errorMessage(r));
      const s = await r.json();
      await loadSubjects();
      set({ subject_id: s.id });
      setNewSubject('');
    } catch (e) { setFlash({ type: 'error', text: `Sujet non créé : ${e.message}` }); }
  };

  // retour à la liste du sujet de la dissertation
  const backTo = form.subject_id ? `/admin/notes/sujet/${form.subject_id}` : '/admin/notes';

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setFlash(null);
    try {
      const r = await authFetch(isNew ? '/api/admin/notes' : `/api/admin/notes/${id}`,
        { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(form) });
      if (!r.ok) throw new Error(await errorMessage(r));
      if (isNew) return navigate(backTo);
      setFlash({ type: 'ok', text: '✓ enregistré' });
      setTimeout(() => setFlash(null), 2000);
    } catch (err) {
      setFlash({ type: 'error', text: `Non enregistré : ${err.message}` });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <h1 style={{ fontStyle: 'italic', marginBottom: '1.5rem' }}>{isNew ? 'Nouvelle dissertation' : 'Éditer la dissertation'}</h1>
      {flash && <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div>}
      <form onSubmit={save}>
        <div className="form-group">
          <label>Sujet</label>
          <div className="note-subject-pick">
            <select value={form.subject_id} onChange={e => set({ subject_id: Number(e.target.value) || '' })} required>
              <option value="">— choisir un sujet —</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input value={newSubject} onChange={e => setNewSubject(e.target.value)} placeholder="ou créer un nouveau sujet" maxLength={100}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubject(); } }} />
            <button type="button" className="btn secondary" onClick={addSubject} disabled={!newSubject.trim()}>+ créer</button>
          </div>
        </div>
        <div className="form-group">
          <label>Titre</label>
          <input value={form.title} onChange={e => set({ title: e.target.value })} required maxLength={200} />
        </div>
        <div className="form-group">
          <label>Contenu <small style={{ color: 'var(--ink-soft)' }}>(les 2 premières lignes serviront d'extrait dans la liste)</small></label>
          <RichEditor value={form.content} onChange={v => set({ content: v })} />
        </div>
        <div className="form-group">
          <label><input type="checkbox" checked={form.published} onChange={e => set({ published: e.target.checked })} /> Publiée</label>
        </div>
        <button className="btn" type="submit" disabled={saving}>{saving ? 'enregistrement…' : 'enregistrer'}</button>
        <button className="btn secondary" type="button" style={{ marginLeft: '.5rem' }} onClick={() => navigate(backTo)}>annuler</button>
      </form>
    </div>
  );
}
